import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  Dispatch,
} from "react";
import { Helmet } from "react-helmet";
import { PaperConfigSizeProps, AppConfig } from "../../../Types";

interface Colors {
  backgroundColor: string;
  textColor: string;
  name: string;
}

interface ProductProviderProps {
  children: JSX.Element;
}

interface ProductContextTypes {
  appConfig: AppConfig | null;
  printPaper: PaperConfigSizeProps | null;
  setPrintPaper: (printPaper: PaperConfigSizeProps) => void;
  previewPaper: PaperConfigSizeProps | null;
  setPreviewPaper: (previewPaper: PaperConfigSizeProps) => void;
  productType: string | null;
  productVariation: string | null;
  setProductVariation: Dispatch<string>;
  productColorType: Colors | null;
  setProductColorType: Dispatch<Colors>;
  productDpi: number | null;
  isExtraProductSelected: boolean;
  setIsExtraProductSelected: Dispatch<boolean>;
}

const PRODUCT_CONTEXT_INITIAL_VALUES = {
  appConfig: null,
  printPaper: null,
  setPrintPaper: (printPaper: PaperConfigSizeProps) => undefined,
  previewPaper: null,
  setPreviewPaper: (previewPaper: PaperConfigSizeProps) => undefined,
  productType: null,
  productVariation: null,
  setProductVariation: (productVariation: string) => undefined,
  productColorType: null,
  setProductColorType: (productColorType: Colors) => undefined,
  productDpi: null,
  isExtraProductSelected: false,
  setIsExtraProductSelected: (isExtraProductSelected: boolean) => undefined,
};

export const ProductContext = createContext<ProductContextTypes>({
  ...PRODUCT_CONTEXT_INITIAL_VALUES,
});

function ProductProvider({ children }: ProductProviderProps) {
  let t = window.INITIAL_DATA.TRANSLATIONS;
  // state
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [printPaper, setPrintPaper] = useState<PaperConfigSizeProps | null>(
    null
  );
  const [previewPaper, setPreviewPaper] = useState<PaperConfigSizeProps | null>(
    null
  );
  const [productType, setProductType] = useState<string | null>(null);
  const [productVariation, setProductVariation] = useState<string | null>(null);
  const [productColorType, setProductColorType] = useState<Colors | null>({
    backgroundColor: "#FFFFFF",
    textColor: "#000000",
    name: "white",
  });
  const [productDpi, setProductDpi] = useState<number | null>(null);
  const [isExtraProductSelected, setIsExtraProductSelected] = useState<boolean>(
    false
  );

  /**
   * @description Hook used to initialize provider initial state.
   */
  useEffect(() => {
    // get paper-type if element with papername-gift id exists
    let rootElement = document.getElementById("papername-gift");
    try {
      setProductType(rootElement.getAttribute("paper-type"));
    } catch (e) {
      console.log(`${e.message} ${t.missing_root_element}`);
    }
  }, []);

  /**
   * @description Hook used to set product paper dpi.
   */
  useEffect(() => {
    if (productType) {
      // require for appConfig
      let config: AppConfig = require("../../../../appConfig.json");
      // if config exists make it available for the app
      try {
        setAppConfig(config);
        setPrintPaper(config.productPaper[productType].printPaper.size);
        setPreviewPaper(config.productPaper[productType].previewPaper.size);
      } catch (e) {
        console.log(`${e.message} ${t.something_not_loaded_correctly}.`);
      }
      try {
        setProductDpi(config.productPaper[productType].printPaper.size.dpi);
        return;
      } catch (e) {
        console.log(`${e.message}. ${t.paper_dpi_config_missing}`);
      }
    }
  }, [productType]);

  const providerValue = {
    appConfig,
    printPaper,
    setPrintPaper,
    previewPaper,
    setPreviewPaper,
    productType,
    productVariation,
    setProductVariation,
    productColorType,
    setProductColorType,
    productDpi,
    isExtraProductSelected,
    setIsExtraProductSelected,
  };

  return (
    <ProductContext.Provider value={providerValue}>
      {appConfig &&
        appConfig[
          window.INITIAL_DATA.LANGUAGE == "ru" ? "cyrillicFonts" : "latinFonts"
        ].length &&
        appConfig[
          window.INITIAL_DATA.LANGUAGE == "ru" ? "cyrillicFonts" : "latinFonts"
        ].map((font, index) => {
          return (
            <Helmet key={`${index}`}>
              <link
                href={`https://fonts.googleapis.com/css?family=${font}&display=swap`}
                rel="stylesheet"
              />
            </Helmet>
          );
        })}
      {children}
    </ProductContext.Provider>
  );
}

export default ProductProvider;
